import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';
import { AuthStore, CachedUserDetails, EventsStore } from '@store';
import { UserRoleType } from '@vi-iot/client';
import {
  CustomTranslationsService,
  CustomerIdentifierType,
} from '../../../../../../operational-data-common/translations';
import { matTooltipSetter } from '../../../../../../shared/functions/mat-tooltip-setter/mat-tooltip-setter';

@Component({
  selector: 'app-customer-identifier',
  templateUrl: './customer-identifier.component.html',
  styleUrls: ['./customer-identifier.component.scss'],
})
export class CustomerIdentifierComponent implements OnInit, OnDestroy {
  readonly CustomerIdentifierType = CustomerIdentifierType;
  readonly UUID_SYSTEM = 'system';
  readonly UUID_COMPANY_SUPPORTER = 'Viessmann';
  readonly UUID_INSTALLATION_USER = 'installationUser';

  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  @Input() uuid: string;

  username: string;
  identifierType: CustomerIdentifierType;
  ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private eventsStore: EventsStore,
    private authStore: AuthStore,
    private cdr: ChangeDetectorRef,
    private customTranslationsService: CustomTranslationsService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadFullUsername();

    this.eventsStore.cachedUsers$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
      this.loadFullUsername();
      this.cdr.markForCheck();
    });
  }

  loadFullUsername(): void {
    if (this.uuid === this.UUID_SYSTEM) {
      this.identifierType = CustomerIdentifierType.System;
      this.username =
        this.translateService.instant(
          this.customTranslationsService.EVENT_LOG_USERNAME[CustomerIdentifierType.System]
        ) ?? '';

      return;
    }

    if (this.uuid === this.UUID_COMPANY_SUPPORTER) {
      this.identifierType = CustomerIdentifierType.Supporter;
      this.username =
        this.translateService.instant(
          this.customTranslationsService.EVENT_LOG_USERNAME[CustomerIdentifierType.Supporter]
        ) ?? '';

      return;
    }

    if (this.uuid === this.UUID_INSTALLATION_USER) {
      this.identifierType = CustomerIdentifierType.User;
      this.username =
        this.translateService.instant(
          this.customTranslationsService.EVENT_LOG_USERNAME[CustomerIdentifierType.User]
        ) ?? '';

      return;
    }

    this.identifierType = CustomerIdentifierType.Admin;
    this.username =
      this.translateService.instant(
        this.customTranslationsService.EVENT_LOG_USERNAME[CustomerIdentifierType.Admin]
      ) ?? '';

    this.authStore
      .isRsaUser()
      .pipe(
        withLatestFrom(this.eventsStore.getCachedUser(this.uuid)),
        take(1),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(([isRsa, user]) => {
        if (user) {
          this.identifierType = this.mapUserToCustomerIdentifiedType(user);
          this.username = isRsa
            ? user.username
            : this.translateService.instant(
                this.customTranslationsService.EVENT_LOG_USERNAME[this.identifierType]
              );
        } else {
          if (isRsa) {
            this.eventsStore.loadUserDetails(this.uuid);
          } else {
            this.identifierType = CustomerIdentifierType.User;
            this.username =
              this.translateService.instant(
                this.customTranslationsService.EVENT_LOG_USERNAME[CustomerIdentifierType.User]
              ) ?? '';
          }
        }
      });
  }

  private mapUserToCustomerIdentifiedType(user: CachedUserDetails): CustomerIdentifierType {
    switch (user.userRole.toLowerCase()) {
      case UserRoleType.endUser.toLowerCase():
        return CustomerIdentifierType.User;

      case UserRoleType.maintainer.toLowerCase():
      case UserRoleType.maintainerDeputy.toLowerCase():
      case UserRoleType.maintainerAdmin.toLowerCase():
      case UserRoleType.maintainerCEO.toLowerCase():
        return CustomerIdentifierType.Maintainer;

      case UserRoleType.supportLocalReadOnly.toLowerCase():
      case UserRoleType.supportLocalReadWrite.toLowerCase():
      case UserRoleType.supportLocalReadWriteWithRemoteDiagnosis.toLowerCase():
      case UserRoleType.supportLocalDepartmentManagement.toLowerCase():
      case UserRoleType.supportInternational.toLowerCase():
      case UserRoleType.supportInternationalWithRemoteDiagnosis.toLowerCase():
        return CustomerIdentifierType.Supporter;

      default:
        return CustomerIdentifierType.User;
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
